import React, { useMemo } from 'react'
import { css } from '@emotion/react'
import { colors, spacing } from '@retailer-platform/shared-components'
import { type CollectionsRoutingConfig } from 'domains/collections/types/collections.types'
import { type CollectionTypeEnum } from 'domains/collections/api'
import { useSearchCollections } from 'domains/collections/api/searchCollections.hooks'
import { CollectionsList } from 'domains/collections/components/collections-list/components/list/CollectionsList'
import { CollectionsListFilters } from 'domains/collections/components/collections-list/components/filters/CollectionsListFilters'
import { useCollectionsListColumns } from 'domains/collections/components/collections-list/utils/collectionsListColumns.hooks'
import { useCollectionsListQueryParams } from 'domains/collections/components/collections-list/utils/collectionsListQueryParams.hooks'

export interface CollectionsListProps {
  routingConfig: CollectionsRoutingConfig
  retailerId?: string
  collectionType: CollectionTypeEnum
}

const pageWrapper = css({
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
})

const filterWrapper = css({
  padding: `${spacing.X16} ${spacing.X24}`,
  borderBottom: `1px solid ${colors.GRAYSCALE.X20}`,
})

const gridWrapper = css({
  flex: 1,
  padding: `0 ${spacing.X20}`,
  overflow: 'hidden',
})

export const CollectionsListContainer: React.FunctionComponent<CollectionsListProps> = ({
  retailerId,
  routingConfig,
  collectionType,
}) => {
  const columns = useCollectionsListColumns({ retailerId, routingConfig })

  const {
    queryParams: filterParams,
    setCollectionName,
    setStatus,
  } = useCollectionsListQueryParams()

  const activeFilter = useMemo(() => {
    if (filterParams.status === 'active') {
      return true
    } else if (filterParams.status === 'inactive') {
      return false
    }
  }, [filterParams.status])

  const { collections, apiLoading, apiError, canLoadMore, loadMore, refetch } =
    useSearchCollections({
      retailerId,
      collectionName: filterParams.collectionName,
      active: activeFilter,
      collectionTypes: [collectionType],
    })

  return (
    <div css={pageWrapper}>
      <div css={filterWrapper}>
        <CollectionsListFilters
          filterParams={filterParams}
          setCollectionName={setCollectionName}
          setStatus={setStatus}
          retailerId={retailerId}
        />
      </div>

      <div css={gridWrapper}>
        <CollectionsList
          columns={columns}
          collections={collections ?? []}
          apiLoading={apiLoading}
          apiError={apiError}
          canLoadMore={canLoadMore}
          loadMore={loadMore}
          refetch={refetch}
        />
      </div>
    </div>
  )
}
